import produce from 'immer';
import {useCallback} from 'react';
import {useSelector} from 'react-redux';

import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {Method} from 'procedures/ProcedureDomainTypes';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {StaticTemplateFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {isStaticTaskSchemaNodeData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {validateFlowchartData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartValidationAndAutosaverControl';
import {useApplicationFlowchartValidationContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartValidationContext';
import {BenchlingState} from 'redux-stores/reduceApp';

export type ValidateBeforeSave = (draftMethod?: Method) => Array<ProcessFlowchartValidationError>;

const EMPTY_FLOWCHART: StaticTemplateFlowchart = {nodesStaticData: [], edgesStaticData: []};

/**
 * Runs validation before and after applying a draft method, and only proceeds with the save
 * if there are zero blocking errors after the edits. If blocking errors exist but were all
 * pre-existing (not introduced by this edit), the save is silently skipped. If new blocking
 * errors were introduced, an error notification is shown.
 *
 */
export async function saveWithValidationGate({
  validateBeforeSave,
  saveImmediately,
  methodData,
}: {
  validateBeforeSave: ValidateBeforeSave;
  saveImmediately: (methodUpdater: (currentMethod: Method) => Method) => Promise<void>;
  methodData: Method;
}): Promise<void> {
  const existingErrors = validateBeforeSave();
  const blockingErrorsBeforeEdits = new Set(
    existingErrors.filter((e) => e.blocksPersistence).map((e) => e.errorMessage)
  );

  const errors = validateBeforeSave(methodData);
  const blockingErrorsAfterEdits = errors.filter((e) => e.blocksPersistence);

  if (blockingErrorsAfterEdits.length === 0) {
    await saveImmediately(() => methodData);
  } else {
    // The toast below isn't terribly helpful, which is why we're pretty conservative with showing it.
    // String matching on the blocking errors is sufficient for this purpose.
    const newlyAddedBlockingErrors = blockingErrorsAfterEdits.filter(
      (e) => !blockingErrorsBeforeEdits.has(e.errorMessage)
    );

    if (newlyAddedBlockingErrors.length > 0) {
      notifications.error(
        'Unable to save: this change introduces validation errors that must be resolved first.'
      );
    }
  }
}

/**
 * Hook that builds a `validateBeforeSave` callback for the MethodSidepanelContext.
 *
 * The callback optionally accepts a draft method. If provided, it merges the draft into the
 * full flowchart (from Redux) before validating. If omitted, it validates the existing
 * flowchart as-is. Either way it runs `validateProcessFlowchart` and returns any
 * `ProcessFlowchartValidationError[]`. This enables pre-save validation that catches
 * cross-method errors (e.g., duplicate names, product limits) that single-node validation
 * misses.
 *
 * But its main purpose is to let us determine if a flowchart is savable at all. Inspecting
 * the callback's return value (the validation errors) for `blocksPersistence`, we can know
 * if new changes remove blocking errors. If they don't, we can retain `autosaver paused`
 * status. If they do, we can clear it and let the save proceed.
 */
export default function useValidateBeforeSave({
  flowchartConfigId,
  selectedNodeId,
}: {
  flowchartConfigId: string;
  selectedNodeId: string;
}): (draftMethod?: Method) => Array<ProcessFlowchartValidationError> {
  const flowchartConfigData = useSelector(
    (state: BenchlingState) => state.models.flowchartConfigs[flowchartConfigId]
  );

  // We need backstop values here, in case the flowchart hasn't been seeded to redux yet
  const {context: applicationValidationContext} = useApplicationFlowchartValidationContext(
    flowchartConfigData?.flowchartConfigMetadata.rootTaskSchema?.executionType ??
      ProcessExecutionType.PROCEDURE,
    flowchartConfigData?.staticFlowchart ?? EMPTY_FLOWCHART
  );

  return useCallback(
    (draftMethod?: Method): Array<ProcessFlowchartValidationError> => {
      if (flowchartConfigData == null) {
        return [];
      }
      const metadata = flowchartConfigData.flowchartConfigMetadata;
      if (metadata.rootTaskSchema == null) {
        return [];
      }

      const plugin = PLUGIN_REGISTRY_INSTANCE.getPluginForFlowchartType(metadata.flowchartTypeName);

      // If a draft method is provided, swap it into the selected node before validating.
      // Otherwise, validate the existing flowchart as-is.
      const staticFlowchart =
        draftMethod != null
          ? produce(flowchartConfigData.staticFlowchart, (draft) => {
              const node = draft.nodesStaticData.find((n) => n.id === selectedNodeId);
              if (node != null && isStaticTaskSchemaNodeData(node)) {
                node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData = draftMethod;
              }
            })
          : flowchartConfigData.staticFlowchart;

      return validateFlowchartData(
        staticFlowchart,
        flowchartConfigId,
        metadata,
        metadata.rootTaskSchema,
        metadata.didInitialFlowchartHaveChainedRouters,
        plugin,
        applicationValidationContext
      );
    },
    [flowchartConfigData, selectedNodeId, flowchartConfigId, applicationValidationContext]
  );
}
